import React, { useMemo, useRef } from 'react'
import QRCode from 'react-qr-code'
import { Button, Panel } from 'rsuite'
import { useReactToPrint } from 'react-to-print'

import { StorageContainer } from '@@partner:types:app'
import { CONTAINER_SIZE_LABELS, CONTAINER_TYPE_LABELS } from '../../../constants/storageContainer'

import './ContainerLabelPrinter.scss'

interface ContainerLabelProps {
  container: StorageContainer
  labelNumber: 1 | 2
}

// Константы размеров в мм – можно будет переиспользовать в SCSS через CSS-переменные
export const LABEL_WIDTH_MM = 190
export const LABEL_HEIGHT_MM = 130
export const PAGE_MARGIN_MM = 10
export const LABEL_GAP_MM = 10

// Компонент одной этикетки (по 2 на контейнер)
function ContainerLabel ({ container }: ContainerLabelProps) {
  const sizeLabel = CONTAINER_SIZE_LABELS[container.size as keyof typeof CONTAINER_SIZE_LABELS] || container.size
  const typeLabel = CONTAINER_TYPE_LABELS[container.type as keyof typeof CONTAINER_TYPE_LABELS] || container.type

  return (
    <div className="ContainerLabel">
      <div className="ContainerLabel__Content">
        <div className="ContainerLabel__QR">
          <QRCode
            value={container.container_id || ''}
            size={256} // большой SVG, размер контролируем через CSS
            level="M"
          />
        </div>
        <div className="ContainerLabel__Info">
          <div className="ContainerLabel__Id">{container.container_id}</div>
          <div className="ContainerLabel__Details">
            <div>{sizeLabel} | {typeLabel}</div>
            {container.description && (
              <div className="ContainerLabel__Description">{container.description}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

interface ContainerLabelPrinterProps {
  containers: StorageContainer[]
  onClose: () => void
}

// Чистый компонент, который будет и для предпросмотра, и для печати
const ContainerLabelsPrintLayout = React.forwardRef<HTMLDivElement, { containers: StorageContainer[] }>(
  ({ containers }, ref) => {
    const sortedContainers = useMemo(
      () => [...containers].sort((a, b) => (a.container_id || '').localeCompare(b.container_id || '')),
      [containers]
    )

    return (
      <div ref={ref} className="ContainerLabelPrinter__PrintRoot">
        <div className="ContainerLabelPrinter__PrintGrid">
          {sortedContainers.map(container => (
            <React.Fragment key={container.id}>
              <ContainerLabel container={container} labelNumber={1} />
              <ContainerLabel container={container} labelNumber={2} />
            </React.Fragment>
          ))}
        </div>
      </div>
    )
  }
)

ContainerLabelsPrintLayout.displayName = 'ContainerLabelsPrintLayout'

export default function ContainerLabelPrinter ({ containers, onClose }: ContainerLabelPrinterProps) {
  const printRef = useRef<HTMLDivElement | null>(null)

  const handlePrint = useReactToPrint({
    contentRef: printRef,           // ✅ v3 API
    documentTitle: 'Этикетки контейнеров',
    // можно добавить onAfterPrint / onBeforePrint и т.д.
  })


  if (containers.length === 0) {
    return (
      <Panel>
        <p>Нет контейнеров для печати этикеток</p>
        <Button onClick={onClose}>Закрыть</Button>
      </Panel>
    )
  }

  const previewContainers = containers.slice(0, 4)

  return (
    <div className="ContainerLabelPrinter">
      {/* Скрытый блок для печати */}
      <div className="ContainerLabelPrinter__HiddenForPrint">
        <ContainerLabelsPrintLayout ref={printRef} containers={containers} />
      </div>

      <Panel header="Печать этикеток контейнеров" bordered>
        <div className="ContainerLabelPrinter__Info">
          <p>Выбрано контейнеров: <strong>{containers.length}</strong></p>
          <p>Будет напечатано: <strong>{containers.length * 2} этикеток</strong> (по 2 на каждый контейнер)</p>
        </div>

        <div className="ContainerLabelPrinter__Preview">
          <h4>Предпросмотр:</h4>
          <div className="ContainerLabelPrinter__PreviewGrid">
            {previewContainers.map(container => (
              <React.Fragment key={container.id}>
                <ContainerLabel container={container} labelNumber={1} />
                <ContainerLabel container={container} labelNumber={2} />
              </React.Fragment>
            ))}
          </div>
          {containers.length > previewContainers.length && (
            <p className="ContainerLabelPrinter__PreviewNote">
              Показаны первые {previewContainers.length} контейнеров
            </p>
          )}
        </div>

        <div className="ContainerLabelPrinter__Actions">
          <Button
            appearance="primary"
            color="green"
            size="lg"
            onClick={handlePrint}
          >
            🖨️ Печать всех этикеток
          </Button>
          <Button
            appearance="subtle"
            size="lg"
            onClick={onClose}
          >
            Отмена
          </Button>
        </div>
      </Panel>
    </div>
  )
}
